import type { RoomMode } from "./types";
import { isAllowedTalkOrigin, talkBrand } from "./brand";
import { ui } from "./ui";

export type AuthContinuation = "start_standard" | "start_private" | "schedule";

const continuationPaths: Record<AuthContinuation, string> = {
  start_standard: "/?continue=start-standard",
  start_private: "/?continue=start-private",
  schedule: "/schedule?continue=save",
};

export function roomStartContinuation(mode: RoomMode): AuthContinuation {
  return mode === "private_1to1" ? "start_private" : "start_standard";
}

export function authContinuationPath(continuation: AuthContinuation): string {
  return continuationPaths[continuation];
}

export function authContinuationFromPath(path: string): AuthContinuation | null {
  try {
    const url = new URL(path, talkBrand.origin);
    if (!isAllowedTalkOrigin(url.origin)) return null;
    if (url.pathname === "/" && url.searchParams.get("continue") === "start-standard") return "start_standard";
    if (url.pathname === "/" && url.searchParams.get("continue") === "start-private") return "start_private";
    if (url.pathname === "/schedule" && url.searchParams.get("continue") === "save") return "schedule";
  } catch {
    // Invalid paths carry no continuation.
  }
  return null;
}

export function authContinuationCopy(continuation: AuthContinuation | null): { contextTitle: string; contextBody: string; formTitle: string; formBody: string } {
  if (continuation === "start_private") return {
    contextTitle: ui("Continue to your private call.", "Продолжить личный звонок."),
    contextBody: ui("Sign in, review the action, then start the verified private 1:1 you selected.", "Войдите, проверьте действие и начните выбранный личный звонок."),
    formTitle: ui("Sign in and continue", "Войти и продолжить"),
    formBody: ui("Your private-call choice is saved.", "Выбор личного звонка сохранён."),
  };
  if (continuation === "start_standard") return {
    contextTitle: ui("Continue to your room.", "Продолжить комнату."),
    contextBody: ui("Sign in, review the action, then start the group room you selected.", "Войдите, проверьте действие и откройте выбранную комнату."),
    formTitle: ui("Sign in and continue", "Войти и продолжить"),
    formBody: ui("Your room choice is saved.", "Выбор комнаты сохранён."),
  };
  if (continuation === "schedule") return {
    contextTitle: ui("Continue scheduling.", "Продолжить расписание."),
    contextBody: ui("Your meeting details stay in this browser while you sign in.", "Черновик встречи останется в этом браузере, пока вы входите."),
    formTitle: ui("Sign in and continue", "Войти и продолжить"),
    formBody: ui("You will return to your saved schedule draft.", "Вы вернётесь к сохранённому черновику."),
  };
  return {
    contextTitle: ui("Return to the room.", "Вернуться в комнату."),
    contextBody: ui(`Your ${talkBrand.organizationName} credentials work here. ${talkBrand.productName} keeps the account session protected in this browser.`, `Аккаунт ${talkBrand.organizationName} работает здесь. Сессия остаётся в этом браузере.`),
    formTitle: ui("Sign in", "Войти"),
    formBody: ui("Use your handle and password.", "Введите имя аккаунта и пароль."),
  };
}
